import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Bookmark,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import type { BookmarkStatus } from '@/types/api';
import { cn } from '@/lib/cn';

interface StatusMeta {
  label: string;
  Icon: LucideIcon;
}

/**
 * Display metadata for each bookmark reading status. The labels are the
 * reader-facing strings for the snake_case enum values stored on the
 * server (`none` | `in_progress` | `completed`).
 */
const STATUS_META: Record<BookmarkStatus, StatusMeta> = {
  none: { label: 'Saved', Icon: Bookmark },
  in_progress: { label: 'In progress', Icon: BookOpen },
  completed: { label: 'Completed', Icon: CheckCircle2 },
};

/** Stable order the options appear in the menu. */
const STATUS_ORDER: BookmarkStatus[] = ['none', 'in_progress', 'completed'];

/** Reader-facing label for a status — reused by callers for toasts etc. */
export const BOOKMARK_STATUS_LABELS: Record<BookmarkStatus, string> = {
  none: STATUS_META.none.label,
  in_progress: STATUS_META.in_progress.label,
  completed: STATUS_META.completed.label,
};

/**
 * Compact dropdown that shows a bookmark's current reading status and lets
 * the reader change it. Rendered inside the Bookshelf card overlay; styled
 * to match the reader-switcher menu in `BrandHeader`.
 *
 * The trigger stops click propagation so tapping it never also opens the
 * BookDetailModal behind the card.
 */
export function BookmarkStatusControl({
  status,
  onChange,
  disabled = false,
}: {
  status: BookmarkStatus;
  onChange: (next: BookmarkStatus) => void;
  disabled?: boolean;
}) {
  const current = STATUS_META[status];
  const CurrentIcon = current.Icon;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Change reading status (currently ${current.label})`}
          className="inline-flex h-8 items-center gap-1 rounded-full border border-or-slate-200 bg-white/95 px-2.5 text-[11px] font-semibold text-or-slate-700 shadow-or-card transition hover:bg-or-slate-50 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-300/60"
        >
          <CurrentIcon className="h-3.5 w-3.5" aria-hidden />
          {current.label}
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[180px] overflow-hidden rounded-2xl border border-or-slate-200 bg-white p-1 shadow-lg"
        >
          {STATUS_ORDER.map((value) => {
            const meta = STATUS_META[value];
            const ItemIcon = meta.Icon;
            const active = value === status;
            return (
              <DropdownMenu.Item
                key={value}
                onSelect={() => {
                  if (!active) onChange(value);
                }}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-[13px] text-or-slate-700 outline-none',
                  'data-[highlighted]:bg-or-orange-50 data-[highlighted]:text-or-orange-800',
                  active && 'bg-or-cream-50 font-semibold',
                )}
              >
                <ItemIcon className="h-4 w-4" aria-hidden />
                <span className="flex-1">{meta.label}</span>
                {active ? <Check className="h-4 w-4 text-or-orange-600" aria-hidden /> : null}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
